<template>
  <v-container>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>Paquetes y precios</v-toolbar-title>
        <v-spacer />
        <v-switch v-model="verInactivos" label="Mostrar inactivos" color="primary" hide-details density="compact"
          class="me-4 flex-grow-0" />
        <v-btn color="primary" variant="flat" class="me-2" @click="abrirItem()">
          {{ TIPOS[tab].nuevo }}
        </v-btn>
      </v-toolbar>

      <v-tabs v-model="tab" color="primary">
        <v-tab v-for="(t, clave) in TIPOS" :key="clave" :value="clave">{{ t.titulo }}</v-tab>
      </v-tabs>
      <v-divider />

      <v-data-table :headers="TIPOS[tab].headers" :items="itemsVisibles" item-key="id" :loading="cargando">
        <template v-slot:[`item.nombre`]="{ item }">
          <span :class="{ 'text-medium-emphasis': !activo(item) }">{{ nombreItem(tab, item) }}</span>
        </template>

        <template v-slot:[`item.categoria`]="{ item }">
          {{ item.categoria_curso?.descripcion }}
        </template>

        <template v-slot:[`item.tipo_cobro`]="{ item }">
          <v-chip size="small" variant="tonal" :color="item.cuantificable ? 'indigo' : 'teal'">
            {{ item.cuantificable ? 'Por horas / clases' : 'Curso completo' }}
          </v-chip>
        </template>

        <template v-slot:[`item.vigentes`]="{ item }">
          <template v-if="preciosVigentes(item).length">
            <v-chip v-for="p in preciosVigentes(item)" :key="p.id" size="small" color="primary" variant="tonal"
              class="me-1 my-1">
              {{ etiquetaPrecio(p) }}
            </v-chip>
          </template>
          <v-chip v-else size="small" color="red" variant="tonal">Sin precio vigente</v-chip>
        </template>

        <template v-slot:[`item.estado`]="{ item }">
          <v-chip size="small" :color="activo(item) ? 'green' : 'grey'" variant="flat">
            {{ activo(item) ? 'Activo' : 'Inactivo' }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-tag-multiple" class="me-2"
            @click="abrirPrecios(item)">
            Precios
          </v-btn>
          <v-icon class="me-2" size="small" title="Editar" @click="abrirItem(item)">mdi-pencil</v-icon>
          <v-icon size="small" :color="activo(item) ? 'red' : 'green'"
            :title="activo(item) ? 'Desactivar' : 'Activar'" @click="toggleItem(item)">
            {{ activo(item) ? 'mdi-eye-off' : 'mdi-eye' }}
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Crear / editar curso, alquiler o servicio -->
    <v-dialog v-model="dialogItem" max-width="560px" persistent>
      <v-card>
        <v-card-title>{{ itemEditado.id ? 'Editar' : TIPOS[tab].nuevo }}</v-card-title>
        <v-card-text>
          <template v-if="tab === 'curso'">
            <v-text-field v-model="itemEditado.descripcion" label="Nombre del curso" />
            <v-select v-model="itemEditado.categoria_curso_id" :items="categorias" item-title="descripcion"
              item-value="id" label="Categoría" />
            <v-switch v-model="itemEditado.cuantificable" color="primary" label="Se cobra por horas / clases"
              :hint="itemEditado.cuantificable
                ? 'El alumno compra una cantidad de horas o clases'
                : 'Precio por el curso completo (sus clases están definidas en el curso)'"
              persistent-hint :disabled="!!itemEditado.id && itemEditado.matriculas_count > 0" />
          </template>

          <v-autocomplete v-else-if="tab === 'alquiler'" v-model="itemEditado.auto_id" :items="autos"
            :item-title="a => `${a.placa} — ${a.marca} ${a.modelo} ${a.color}`" item-value="id" label="Auto" />

          <v-text-field v-else v-model="itemEditado.descripcion" label="Nombre del servicio" />

          <template v-if="!itemEditado.id">
            <v-divider class="my-4" />
            <div class="text-subtitle-2 mb-2">Primer precio (opcional)</div>
            <v-row dense>
              <v-col v-if="tab === 'curso'" cols="12" md="7">
                <v-text-field v-model="itemEditado.precio_descripcion" label="Nombre del paquete"
                  placeholder="Ej. Precio regular" />
              </v-col>
              <v-col cols="12" :md="tab === 'curso' ? 5 : 12">
                <v-text-field v-model.number="itemEditado.precio" type="number" min="0" prefix="S/" label="Precio" />
              </v-col>
            </v-row>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogItem = false">Cancelar</v-btn>
          <v-btn color="primary" variant="flat" :loading="guardando" :disabled="!itemValido" @click="confirmarItem">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Precios / paquetes de un ítem -->
    <v-dialog v-model="dialogPrecios" max-width="900px" scrollable>
      <v-card v-if="itemPrecios">
        <v-card-title class="d-flex align-center">
          <span>Precios — {{ nombreItem(tab, itemPrecios) }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="dialogPrecios = false" />
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Solo los precios <strong>vigentes</strong> se ofrecen al matricular o cotizar. Si cambias el monto
            {{ esCursoPorHoras ? 'o la cantidad ' : '' }}se crea una versión nueva y la anterior queda en el
            historial: las matrículas ya hechas conservan su precio.
          </v-alert>

          <v-table density="compact">
            <thead>
              <tr>
                <th>{{ tab === 'curso' ? 'Paquete' : 'Descripción' }}</th>
                <th v-if="esCursoPorHoras">Cantidad</th>
                <th class="text-right">Precio</th>
                <th>Estado</th>
                <th v-if="tab === 'curso'" class="text-center">Matrículas</th>
                <th>Desde</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in preciosDelItem" :key="p.id" :class="{ 'text-medium-emphasis': !p.activo }">
                <td>{{ p.descripcion || '—' }}</td>
                <td v-if="esCursoPorHoras">{{ p.cantidad ? `${p.cantidad} h/clases` : 'Por unidad' }}</td>
                <td class="text-right font-weight-medium">{{ soles(p.precio) }}</td>
                <td>
                  <v-chip size="x-small" :color="p.activo ? 'green' : 'grey'" variant="flat">
                    {{ p.activo ? 'Vigente' : 'Historial' }}
                  </v-chip>
                </td>
                <td v-if="tab === 'curso'" class="text-center">{{ p.matriculas_count ?? 0 }}</td>
                <td>{{ fecha(p.created_at) }}</td>
                <td class="text-no-wrap">
                  <v-icon v-if="p.activo" size="small" class="me-2" title="Editar" @click="editarPrecio(p)">
                    mdi-pencil
                  </v-icon>
                  <v-icon size="small" :color="p.activo ? 'red' : 'green'"
                    :title="p.activo ? 'Retirar (pasa al historial)' : 'Volver a ofrecer'"
                    @click="togglePrecio(p)">
                    {{ p.activo ? 'mdi-archive-arrow-down' : 'mdi-archive-arrow-up' }}
                  </v-icon>
                </td>
              </tr>
              <tr v-if="!preciosDelItem.length">
                <td colspan="7" class="text-center text-medium-emphasis py-4">Aún no tiene precios.</td>
              </tr>
            </tbody>
          </v-table>

          <v-divider class="my-4" />
          <div class="text-subtitle-2 mb-2">
            {{ precioEditado.id ? 'Editar precio' : (tab === 'curso' ? 'Agregar paquete' : 'Agregar precio') }}
          </div>
          <v-row dense align="center">
            <v-col cols="12" :md="esCursoPorHoras ? 5 : 7">
              <v-text-field v-model="precioEditado.descripcion"
                :label="tab === 'curso' ? 'Nombre del paquete' : 'Descripción (opcional)'"
                :placeholder="esCursoPorHoras ? 'Ej. Paquete 10 horas' : 'Ej. Precio regular'" density="compact" />
            </v-col>
            <v-col v-if="esCursoPorHoras" cols="6" md="2">
              <v-text-field v-model.number="precioEditado.cantidad" type="number" min="1" label="Cantidad"
                hint="Vacío = precio por hora/clase" persistent-hint density="compact" />
            </v-col>
            <v-col :cols="esCursoPorHoras ? 6 : 12" :md="esCursoPorHoras ? 2 : 3">
              <v-text-field v-model.number="precioEditado.precio" type="number" min="0" prefix="S/"
                :label="esCursoPorHoras && !precioEditado.cantidad ? 'Precio por unidad' : 'Precio'"
                density="compact" />
            </v-col>
            <v-col cols="12" md="3" class="d-flex ga-2">
              <v-btn color="primary" variant="flat" :loading="guardando" :disabled="!precioValido"
                @click="confirmarPrecio">
                {{ precioEditado.id ? 'Guardar' : 'Agregar' }}
              </v-btn>
              <v-btn v-if="precioEditado.id" variant="text" @click="limpiarPrecio">Cancelar</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

const TIPOS = {
  curso: {
    titulo: 'Cursos',
    nuevo: 'Nuevo curso',
    lista: 'cursos',
    headers: [
      { title: 'Curso', value: 'nombre', sortable: false },
      { title: 'Categoría', value: 'categoria', sortable: false },
      { title: 'Cobro', value: 'tipo_cobro', sortable: false },
      { title: 'Precios vigentes', value: 'vigentes', sortable: false },
      { title: 'Matrículas', value: 'matriculas_count' },
      { title: 'Estado', value: 'estado', sortable: false },
      { title: '', value: 'actions', sortable: false, align: 'end' },
    ],
  },
  alquiler: {
    titulo: 'Alquileres',
    nuevo: 'Nuevo alquiler',
    lista: 'alquileres',
    headers: [
      { title: 'Auto', value: 'nombre', sortable: false },
      { title: 'Precios vigentes', value: 'vigentes', sortable: false },
      { title: 'Estado', value: 'estado', sortable: false },
      { title: '', value: 'actions', sortable: false, align: 'end' },
    ],
  },
  'servicio-externo': {
    titulo: 'Servicios externos',
    nuevo: 'Nuevo servicio',
    lista: 'servicios',
    headers: [
      { title: 'Servicio', value: 'nombre', sortable: false },
      { title: 'Precios vigentes', value: 'vigentes', sortable: false },
      { title: 'Estado', value: 'estado', sortable: false },
      { title: '', value: 'actions', sortable: false, align: 'end' },
    ],
  },
};

export default {
  data() {
    return {
      TIPOS,
      tab: 'curso',
      verInactivos: false,
      cargando: false,
      guardando: false,
      dialogItem: false,
      itemEditado: {},
      dialogPrecios: false,
      itemPreciosId: null,
      precioEditado: {},
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    ...mapGetters('paquetes', ['categorias', 'cursos', 'alquileres', 'servicios']),
    ...mapGetters('gasto', ['autos']),
    itemsDelTab() {
      return this[TIPOS[this.tab].lista] || [];
    },
    itemsVisibles() {
      return this.verInactivos ? this.itemsDelTab : this.itemsDelTab.filter(this.activo);
    },
    // Se busca en la lista para que se refresque tras cada cambio
    itemPrecios() {
      return this.itemsDelTab.find(i => i.id === this.itemPreciosId) || null;
    },
    preciosDelItem() {
      return this.itemPrecios?.precio || [];
    },
    esCursoPorHoras() {
      return this.tab === 'curso' && !!this.itemPrecios?.cuantificable;
    },
    itemValido() {
      const i = this.itemEditado;
      const precioOk = i.precio === '' || i.precio == null || i.precio >= 0;
      if (this.tab === 'curso') {
        return !!i.descripcion && !!i.categoria_curso_id && precioOk
          && (i.precio === '' || i.precio == null || !!i.precio_descripcion);
      }
      if (this.tab === 'alquiler') return !!i.auto_id && precioOk;
      return !!i.descripcion && precioOk;
    },
    precioValido() {
      const p = this.precioEditado;
      return p.precio !== '' && p.precio != null && p.precio >= 0
        && (this.tab !== 'curso' || !!p.descripcion)
        && (!p.cantidad || p.cantidad >= 1);
    },
  },
  watch: {
    tab() {
      this.dialogPrecios = false;
    },
  },
  async created() {
    this.cargando = true;
    try {
      await this.fetchCatalogo();
    } catch (e) {
      this.notificarError(e);
    } finally {
      this.cargando = false;
    }
    this.fetchAutosForGasto();
  },
  methods: {
    ...mapActions('paquetes', [
      'fetchCatalogo', 'guardarItem', 'cambiarEstadoItem',
      'agregarPrecio', 'actualizarPrecio', 'cambiarEstadoPrecio',
    ]),
    ...mapActions('gasto', ['fetchAutosForGasto']),

    activo(item) {
      return item.estado === true || item.estado === 1 || item.estado === '1';
    },
    nombreItem(tipo, item) {
      if (tipo === 'alquiler') {
        const a = item.auto || {};
        return `${a.placa || ''} — ${a.marca || ''} ${a.modelo || ''} ${a.color || ''}`.trim();
      }
      return item.descripcion;
    },
    preciosVigentes(item) {
      return (item.precio || []).filter(p => p.activo);
    },
    etiquetaPrecio(p) {
      const nombre = p.descripcion ? `${p.descripcion} · ` : '';
      const unidad = this.tab === 'curso' && this.itemEsPorHoras(p) && !p.cantidad ? ' c/u' : '';
      return `${nombre}${this.soles(p.precio)}${unidad}`;
    },
    itemEsPorHoras(precio) {
      return !!this.cursos.find(c => c.id === precio.curso_id)?.cuantificable;
    },
    soles(v) {
      return `S/ ${Number(v || 0).toFixed(2)}`;
    },
    fecha(v) {
      return v ? moment(v).format('DD/MM/YYYY') : '';
    },
    notificar(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
    notificarError(e) {
      const errores = e.response?.data?.errors;
      const primero = errores ? Object.values(errores)[0]?.[0] : null;
      this.notificar(primero || e.response?.data?.message || 'Ocurrió un error', 'red');
    },

    /* ítems */
    abrirItem(item = null) {
      this.itemEditado = item
        ? {
          id: item.id,
          descripcion: item.descripcion,
          categoria_curso_id: item.categoria_curso_id,
          cuantificable: !!item.cuantificable,
          auto_id: item.auto_id,
          matriculas_count: item.matriculas_count,
        }
        : { cuantificable: false, precio: null, precio_descripcion: 'Precio regular' };
      this.dialogItem = true;
    },
    async confirmarItem() {
      const i = this.itemEditado;
      let item = {};
      if (this.tab === 'curso') {
        item = { id: i.id, descripcion: i.descripcion, categoria_curso_id: i.categoria_curso_id, cuantificable: i.cuantificable };
      } else if (this.tab === 'alquiler') {
        item = { id: i.id, auto_id: i.auto_id };
      } else {
        item = { id: i.id, descripcion: i.descripcion };
      }
      if (!i.id && i.precio !== '' && i.precio != null) {
        item.precio = i.precio;
        if (this.tab === 'curso') item.precio_descripcion = i.precio_descripcion;
      }

      this.guardando = true;
      try {
        await this.guardarItem({ tipo: this.tab, item });
        this.dialogItem = false;
        this.notificar('Guardado');
      } catch (e) {
        this.notificarError(e);
      } finally {
        this.guardando = false;
      }
    },
    async toggleItem(item) {
      try {
        await this.cambiarEstadoItem({ tipo: this.tab, id: item.id, estado: !this.activo(item) });
        this.notificar(this.activo(item) ? 'Desactivado: ya no se ofrece' : 'Activado');
      } catch (e) {
        this.notificarError(e);
      }
    },

    /* precios */
    abrirPrecios(item) {
      this.itemPreciosId = item.id;
      this.limpiarPrecio();
      this.dialogPrecios = true;
    },
    limpiarPrecio() {
      this.precioEditado = { id: null, descripcion: '', cantidad: null, precio: null };
    },
    editarPrecio(p) {
      this.precioEditado = {
        id: p.id,
        descripcion: p.descripcion || '',
        cantidad: p.cantidad,
        precio: Number(p.precio),
      };
    },
    async confirmarPrecio() {
      const p = this.precioEditado;
      const precio = { descripcion: p.descripcion || null, precio: p.precio };
      if (this.esCursoPorHoras) precio.cantidad = p.cantidad || null;

      this.guardando = true;
      try {
        if (p.id) {
          await this.actualizarPrecio({ tipo: this.tab, precioId: p.id, precio });
          this.notificar('Precio actualizado');
        } else {
          await this.agregarPrecio({ tipo: this.tab, itemId: this.itemPreciosId, precio });
          this.notificar('Precio agregado');
        }
        this.limpiarPrecio();
      } catch (e) {
        this.notificarError(e);
      } finally {
        this.guardando = false;
      }
    },
    async togglePrecio(p) {
      try {
        await this.cambiarEstadoPrecio({ tipo: this.tab, precioId: p.id, activo: !p.activo });
        this.notificar(p.activo ? 'Precio retirado' : 'Precio vigente de nuevo');
      } catch (e) {
        this.notificarError(e);
      }
    },
  },
};
</script>
